import { createContext, useContext, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getRequest, postRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import Swal from "sweetalert2";

const CashierHistoryContext = createContext();

export const CashierHistoryProvider = ({ children }) => {
  const queryClient = useQueryClient();

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    filters: {},
    include: "cashier"
  });

  const queryKey = useMemo(
    () => ["cashier-sessions", filterOptions.search, filterOptions.currentPage],
    [filterOptions.search, filterOptions.currentPage]
  );
  

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    paginate: 15,
    // first:1,
    ...(filterOptions.filters["categories.id"] && {
      [`filter[categories.id]`]: filterOptions.filters["categories.id"],
    }),
    include: filterOptions.include,
  });

  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      // const query = buildQueryString({ search: filterOptions.search || "" });
      const res = await getRequest(`/cashier-session${queryString}`);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch cashier sessions");
      }
      return res;
    },
    keepPreviousData: true,
  });

  const addCashierSessionMutation = useMutation({
    mutationFn: async (newSession) => {
      return await postRequest(`/cashier-session`, { name: newSession });
    },
    onSuccess: () => {
      Swal.fire("Success", "Cashier session added successfully!", "success");
      queryClient.invalidateQueries({ queryKey: ["cashier-sessions"] });
    },
    onError: (err) => {
      Swal.fire(
        "Error",
        err.message || "Failed to add cashier session",
        "error"
      );
    },
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
        currentPage: 1,
      }));
    }
  };

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = response?.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <CashierHistoryContext.Provider
      value={{
        cashierSessions: response?.data || [],
        isLoading,
        isError,
        error,
        filterOptions,
        handleSearch,
        refetchCashierSessions: refetch,
        Pagination,
        addCashierSession: addCashierSessionMutation.mutate,
      }}
    >
      {children}
    </CashierHistoryContext.Provider>
  );
};

export const useCashierSession = () => {
  const context = useContext(CashierHistoryContext);
  if (!context) {
    throw new Error(
      "useCashierSession must be used within CashierSessionProvider"
    );
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/cashier-session?search=${inputValue}`);
    if (response.success) {
      return response.data.map((session) => ({
        value: session.id,
        label: session.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};
